// Componente MultiSelect - Combobox con selección múltiple y búsqueda
// Componente reutilizable para seleccionar múltiples opciones con búsqueda

import { useState, useRef, useEffect } from 'react'
import './MultiSelect.css'

/**
 * Componente MultiSelect
 * @param {Array} options - Array de opciones {id, label, sublabel?}
 * @param {Array} selected - Array de IDs seleccionados
 * @param {Function} onChange - Callback cuando cambia la selección (recibe array de IDs)
 * @param {string} placeholder - Texto placeholder
 * @param {boolean} disabled - Si está deshabilitado
 * @param {string} searchPlaceholder - Placeholder para el buscador
 */
function MultiSelect({
  options = [],
  selected,
  selectedValues,
  onChange,
  placeholder = 'Seleccionar...',
  disabled = false,
  searchPlaceholder = 'Buscar...'
}) {
  const [isOpen, setIsOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const containerRef = useRef(null)
  const selectedIds = Array.isArray(selected) ? selected : (Array.isArray(selectedValues) ? selectedValues : [])

  const normalizedOptions = Array.isArray(options)
    ? options.map((option, index) => {
      const rawId = option?.id ?? option?.value ?? option?.key
      const id = rawId ?? `${option?.label || option?.name || 'opt'}:${index}`
      const label = typeof option?.label === 'string' && option.label.trim()
        ? option.label
        : (typeof option?.name === 'string' && option.name.trim() ? option.name : String(id))
      const sublabel = typeof option?.sublabel === 'string' && option.sublabel.trim()
        ? option.sublabel
        : (typeof option?.subtitle === 'string' && option.subtitle.trim() ? option.subtitle : '')
      return { ...option, id, label, sublabel }
    })
    : []

  // Filtrar opciones por búsqueda
  const filteredOptions = normalizedOptions.filter(option => {
    if (!searchTerm.trim()) return true
    const search = searchTerm.toLowerCase()
    const label = (option.label || '').toLowerCase()
    const sublabel = (option.sublabel || '').toLowerCase()
    return label.includes(search) || sublabel.includes(search)
  })

  // Manejar clic fuera para cerrar
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false)
        setSearchTerm('')
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  // Toggle selección de una opción
  const toggleOption = (optionId) => {
    if (disabled) return
    if (typeof onChange !== 'function') return

    const newSelected = selectedIds.includes(optionId)
      ? selectedIds.filter(id => id !== optionId)
      : [...selectedIds, optionId]

    onChange(newSelected)
  }

  // Remover una opción seleccionada
  const removeOption = (optionId, e) => {
    e.stopPropagation()
    if (disabled) return
    if (typeof onChange !== 'function') return

    const newSelected = selectedIds.filter(id => id !== optionId)
    onChange(newSelected)
  }

  // Obtener opciones seleccionadas para mostrar
  const selectedOptions = normalizedOptions.filter(opt => selectedIds.includes(opt.id))

  return (
    <div className="multi-select-container" ref={containerRef}>
      {/* Input principal */}
      <div
        className={`multi-select-input ${isOpen ? 'open' : ''} ${disabled ? 'disabled' : ''}`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
      >
        <div className="multi-select-selected-tags">
          {selectedOptions.length > 0 ? (
            selectedOptions.map(option => (
              <span key={option.id} className="selected-tag">
                {option.label}
                {!disabled && (
                  <button
                    type="button"
                    className="tag-remove"
                    onClick={(e) => removeOption(option.id, e)}
                    title="Remover"
                  >
                    ×
                  </button>
                )}
              </span>
            ))
          ) : (
            <span className="multi-select-placeholder">{placeholder}</span>
          )}
        </div>
        <div className="multi-select-arrow">
          {isOpen ? '▲' : '▼'}
        </div>
      </div>

      {/* Dropdown */}
      {isOpen && !disabled && (
        <div className="multi-select-dropdown">
          {/* Buscador */}
          <div className="multi-select-search">
            <input
              type="text"
              className="multi-select-search-input"
              placeholder={searchPlaceholder}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              autoFocus
            />
          </div>

          {/* Lista de opciones */}
          <div className="multi-select-options">
            {filteredOptions.length === 0 ? (
              <div className="multi-select-no-results">
                No se encontraron resultados
              </div>
            ) : (
              filteredOptions.map(option => {
                const isSelected = selectedIds.includes(option.id)
                return (
                  <div
                    key={option.id}
                    className={`multi-select-option ${isSelected ? 'selected' : ''}`}
                    onClick={() => toggleOption(option.id)}
                  >
                    <div className="multi-select-option-content">
                      <span className="multi-select-option-label">{option.label}</span>
                      {option.sublabel && (
                        <span className="multi-select-option-sublabel">{option.sublabel}</span>
                      )}
                    </div>
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleOption(option.id)}
                      className="multi-select-checkbox"
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                )
              })
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default MultiSelect

